import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PublicChatWidget from "@/components/PublicChatWidget";
import ROICalculator from "@/components/ROICalculator";
import { toast, Toaster } from "sonner";
import { ArrowUpRight, Check, ChevronDown, X, CreditCard, Lock, RotateCw } from "lucide-react";
import {
  T, FONT_DISPLAY, FONT_MONO, PageShell, TopNav, Footer,
  container, eyebrow, h2, italicAccent, btnCoral, input as inputStyle,
} from "@/lib/hybridTheme";

/** Public → Stripe lookup_key mapping. Prices are configured in Stripe;
 *  what shows on this page is display-only. Stripe Checkout is authoritative. */
const LOOKUP_KEYS = {
  starter:        "starter_monthly",
  front_office:   "growth_monthly",
  multi_location: "elite_monthly",
};

const PLANS = [
  { key: "starter",       name: "Starter",        price: 397, tag: "AI chat on your site",   setup: 750,
    bullets: ["Website preview (yours to keep)", "500 outbound emails / mo", "Basic pipeline", "Business-hours support"] },
  { key: "front_office",  name: "Front Office",   price: 697, tag: "The one everyone picks", setup: 1200,
    bullets: ["24/7 voice agent", "Auto-booking to your calendar", "5,000 emails + SMS / mo", "Automated follow-up sequences", "Priority support"], best: true },
  { key: "multi_location",name: "Multi-Location", price: 997, tag: "2-5 locations",          setup: 1800,
    bullets: ["Unlimited AI agents", "Per-location routing & analytics", "Done-for-you outreach", "Dedicated ops manager", "Priority support"] },
];

/** Curated signup add-ons shown inside the checkout modal.
 *  `setup` is one-time (adds to Due today). `monthly` is recurring (adds to Monthly).
 *  IDs match /app/backend/addons.py ADDON_CATALOG so activation on payment success just works. */
const SIGNUP_ADDONS = [
  {
    id: "live_site_upgrade",
    name: "Live Site Upgrade — 11-section version",
    blurb: "Full conversion-optimized site: pain-stat hero, timeline, comparison, testimonials, FAQ, service areas, guarantee, gallery & reviews widget.",
    setup: 69, monthly: 0,
    highlight: true,
  },
  {
    id: "missed_call_textback",
    name: "Missed-Call Auto Text-Back",
    blurb: "Every missed call gets an instant SMS. Recovers ~15% of dropped leads.",
    setup: 0, monthly: 29,
  },
  {
    id: "google_reviews_widget",
    name: "Google Reviews Widget",
    blurb: "Live 5-star reviews auto-imported onto your site — builds trust in seconds.",
    setup: 0, monthly: 19,
  },
  {
    id: "review_automation",
    name: "Review Request Automation",
    blurb: "Auto-text every customer after service. Climbs Google Maps.",
    setup: 0, monthly: 29,
  },
];

const FAQS = [
  { q: "Do I need a website already?", a: "No. Every plan includes a free preview website we build for you in 24 hours — yours to keep, whether or not you continue." },
  { q: "How does the AI agent sound?", a: "Natural, human-like voice. 80% of callers don't realize they're talking to AI. Try our chat widget bottom-right to see." },
  { q: "What if I want to cancel?", a: "Month-to-month, 30-day notice. No cancellation fee. No contracts." },
  { q: "What if the AI can't answer something?", a: "It gracefully takes a message with the caller's name, phone, and reason — texted straight to you within 60 seconds." },
  { q: "Do you handle Spanish (or other languages)?", a: "Yes. Standard on Front Office and Multi-Location. Contact us for others." },
  { q: "How do you charge — subscription or per call?", a: "Flat monthly, no per-call fees. What's included is on this page. Overages are billed at cost with no markup." },
];

export default function Pricing() {
  const [agent, setAgent] = useState(null);
  useEffect(() => { api.get("/public/site-config").then(({ data }) => setAgent(data.agent || null)).catch(() => {}); }, []);
  const [openFaq, setOpenFaq] = useState(0);

  // Stripe checkout modal state
  const [checkoutPlan, setCheckoutPlan] = useState(null); // {key, name, price, ...}
  const [form, setForm] = useState({ email: "", contact_name: "", business_name: "", phone: "" });
  const [selectedAddons, setSelectedAddons] = useState({}); // {[addon_id]: true}
  const [submitting, setSubmitting] = useState(false);

  const openCheckout = (plan) => {
    setForm({ email: "", contact_name: "", business_name: "", phone: "" });
    setSelectedAddons({});
    setCheckoutPlan(plan);
  };

  const toggleAddon = (id) => setSelectedAddons((s) => ({ ...s, [id]: !s[id] }));

  const activeAddons = SIGNUP_ADDONS.filter((a) => selectedAddons[a.id]);
  const addonsSetupTotal   = activeAddons.reduce((sum, a) => sum + (a.setup || 0), 0);
  const addonsMonthlyTotal = activeAddons.reduce((sum, a) => sum + (a.monthly || 0), 0);

  const submitCheckout = async (e) => {
    e?.preventDefault();
    if (!checkoutPlan) return;
    const email = form.email.trim();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Please enter a valid email.");
      return;
    }
    const lookup = LOOKUP_KEYS[checkoutPlan.key];
    if (!lookup) {
      toast.error("This plan isn't wired to Stripe yet. Contact us to get set up.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/checkout", {
        lookup_key:    lookup,
        origin_url:    window.location.origin,
        email,
        contact_name:  form.contact_name.trim(),
        business_name: form.business_name.trim(),
        phone:         form.phone.trim(),
        addons:        activeAddons.map((a) => a.id),
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Stripe didn't return a checkout URL.");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell testId="pricing-public-page">
      <TopNav activeKey="pricing" />

      <section style={{ padding: "112px 0 56px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -80, right: -100, width: 600, height: 600, background: "radial-gradient(circle, rgba(229,83,59,0.08) 0%, transparent 60%)", pointerEvents: "none", filter: "blur(50px)" }} />
        <div style={container()}>
          <div style={{ maxWidth: 900, position: "relative" }}>
            <div style={eyebrow()}>Pricing</div>
            <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(52px, 6.5vw, 96px)", lineHeight: 0.98, letterSpacing: "-0.035em", fontWeight: 400, margin: "16px 0 24px", color: T.ink }}>
              One monthly rate.<br />
              <em style={italicAccent(T.coral)}>No contract.</em>
            </h1>
            <p style={{ fontSize: 20, lineHeight: 1.55, color: T.inkMuted, maxWidth: 640, margin: 0 }}>
              Month-to-month, 30-day out. Setup fee once, then the same monthly rate every month. No per-call fees, no hidden charges.
            </p>
          </div>
        </div>
      </section>

      <section id="plans" style={{ padding: "56px 0 88px" }}>
        <div style={container()}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 20 }}>
            {PLANS.map((p) => {
              const isBest = !!p.best;
              const accent = p.key === "front_office" ? T.coral : p.key === "multi_location" ? T.sage : T.brass;
              const bg = isBest ? T.ink : T.panel;
              const ink = isBest ? T.cream : T.ink;
              return (
                <div key={p.key} data-testid={`pricing-plan-${p.key}`} style={{
                  background: bg, color: ink, padding: "40px 32px",
                  border: `1px solid ${isBest ? T.ink : T.line}`, position: "relative",
                  boxShadow: isBest ? "0 30px 60px -20px rgba(10,25,48,0.35)" : "none",
                  transform: isBest ? "scale(1.02)" : "none",
                }}>
                  {isBest && <div style={{ position: "absolute", top: -1, left: -1, background: accent, color: T.cream, padding: "6px 14px", fontSize: 11, fontFamily: FONT_MONO, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 700 }}>Best fit</div>}
                  <div style={{ fontSize: 12, fontFamily: FONT_MONO, letterSpacing: "0.09em", textTransform: "uppercase", color: accent, fontWeight: 600 }}>{p.tag}</div>
                  <div style={{ fontFamily: FONT_DISPLAY, fontSize: 36, fontWeight: 400, marginTop: 10, fontStyle: "italic", color: ink }}>{p.name}</div>
                  <div style={{ marginTop: 24, display: "flex", alignItems: "baseline", gap: 8 }}>
                    <div style={{ fontFamily: FONT_DISPLAY, fontSize: 76, fontWeight: 400, letterSpacing: "-0.03em", lineHeight: 1, color: ink }}>${p.price}</div>
                    <div style={{ color: isBest ? "rgba(247,244,238,0.65)" : T.inkFaint, fontSize: 14 }}>/mo</div>
                  </div>
                  <div style={{ fontSize: 13, color: isBest ? "rgba(247,244,238,0.6)" : T.inkFaint, marginTop: 6 }}>
                    + ${p.setup} one-time setup
                  </div>
                  <ul style={{ listStyle: "none", padding: 0, margin: "28px 0 32px", fontSize: 14 }}>
                    {p.bullets.map((f) => (
                      <li key={f} style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 12, lineHeight: 1.55 }}>
                        <Check size={16} style={{ marginTop: 2, color: accent, flexShrink: 0 }} strokeWidth={2.5} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => openCheckout(p)}
                    data-testid={`pricing-cta-${p.key}`}
                    style={{
                      background: isBest ? accent : "transparent",
                      color: isBest ? T.cream : T.ink,
                      border: isBest ? "none" : `1px solid ${T.ink}`,
                      padding: "14px 22px", fontSize: 14, fontWeight: 700, cursor: "pointer",
                      display: "inline-flex", alignItems: "center", gap: 8, width: "100%",
                      justifyContent: "center", boxSizing: "border-box", fontFamily: "inherit",
                    }}
                  >
                    Start with {p.name} <ArrowUpRight size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section style={{ padding: "88px 0", background: T.canvas, borderTop: `1px solid ${T.lineSoft}`, borderBottom: `1px solid ${T.lineSoft}` }}>
        <div style={container()}>
          <div style={{ maxWidth: 720, marginBottom: 40 }}>
            <div style={eyebrow()}>Your numbers</div>
            <h2 style={h2()}>What are missed calls <em style={italicAccent(T.coral)}>actually</em> costing you?</h2>
          </div>
          <div style={{ background: T.cream, color: T.ink, padding: "40px 44px", border: `1px solid ${T.line}` }}>
            <ROICalculator />
          </div>
        </div>
      </section>

      <section style={{ padding: "96px 0" }}>
        <div style={container()}>
          <div style={{ maxWidth: 780, marginBottom: 32 }}>
            <div style={eyebrow()}>FAQ</div>
            <h2 style={h2()}>Answers to the <em style={italicAccent(T.brass)}>obvious</em> questions.</h2>
          </div>
          <div style={{ maxWidth: 820 }}>
            {FAQS.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={i} style={{ borderBottom: `1px solid ${T.line}` }}>
                  <button
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    data-testid={`faq-toggle-${i}`}
                    style={{ width: "100%", background: "transparent", border: "none", padding: "22px 0", textAlign: "left", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 24, fontFamily: "inherit" }}
                  >
                    <span style={{ fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 500, color: T.ink }}>{f.q}</span>
                    <ChevronDown size={20} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 160ms", color: T.coral, flexShrink: 0 }} />
                  </button>
                  {open && <p style={{ fontSize: 16, color: T.inkMuted, lineHeight: 1.6, padding: "0 0 22px", margin: 0 }}>{f.a}</p>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section style={{ padding: "72px 0 120px" }}>
        <div style={container()}>
          <div style={{ background: T.ink, color: T.cream, padding: "56px 48px", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: -80, right: -80, width: 320, height: 320, background: `radial-gradient(circle, ${T.coral} 0%, transparent 65%)`, opacity: 0.55, borderRadius: "50%" }} />
            <div style={{ position: "relative", display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 40, alignItems: "center" }}>
              <div>
                <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: 48, fontWeight: 400, letterSpacing: "-0.03em", lineHeight: 1, margin: 0 }}>
                  Stop losing the<br /><em style={italicAccent(T.coral)}>phone lottery.</em>
                </h3>
                <p style={{ marginTop: 20, maxWidth: 460, lineHeight: 1.6, opacity: 0.75 }}>
                  Book a 15-minute demo. Free preview website inside 24 hours. Zero commitment.
                </p>
              </div>
              <div>
                <Link to="/#contact" style={{ ...btnCoral(), padding: "18px 28px", fontSize: 15, textDecoration: "none" }}>
                  Book my demo <ArrowUpRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <PublicChatWidget agent={agent} />
      <Toaster position="top-right" richColors />

      {/* Stripe Checkout modal */}
      {checkoutPlan && (
        <div
          data-testid="pricing-checkout-modal"
          onClick={() => !submitting && setCheckoutPlan(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 100,
            background: "rgba(10,25,48,0.72)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: T.cream, color: T.ink, width: "100%", maxWidth: 520,
              border: `1px solid ${T.ink}`, boxShadow: "0 40px 80px -20px rgba(0,0,0,0.5)",
              animation: "co-fade-in 200ms ease-out both",
            }}
          >
            {/* Modal header */}
            <div style={{ padding: "22px 28px", borderBottom: `1px solid ${T.lineSoft}`, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, background: T.ink, color: T.cream }}>
              <div>
                <div style={{ fontSize: 11, fontFamily: FONT_MONO, letterSpacing: "0.12em", textTransform: "uppercase", color: T.brass, fontWeight: 700 }}>
                  Start subscription
                </div>
                <div style={{ fontFamily: FONT_DISPLAY, fontSize: 26, marginTop: 6, fontStyle: "italic" }}>
                  {checkoutPlan.name}
                </div>
                <div style={{ fontSize: 13, opacity: 0.7, marginTop: 4, fontFamily: FONT_MONO }}>
                  ${checkoutPlan.price}/mo · ${checkoutPlan.setup} one-time setup
                </div>
              </div>
              <button
                onClick={() => !submitting && setCheckoutPlan(null)}
                aria-label="Close"
                data-testid="pricing-checkout-close"
                style={{ background: "transparent", border: "none", color: T.cream, cursor: submitting ? "wait" : "pointer", padding: 4 }}
              >
                <X size={22} />
              </button>
            </div>

            <form onSubmit={submitCheckout} style={{ padding: "24px 28px", display: "grid", gap: 12 }}>
              <label style={{ display: "grid", gap: 6 }}>
                <span style={{ fontSize: 11, fontFamily: FONT_MONO, letterSpacing: "0.1em", textTransform: "uppercase", color: T.inkFaint }}>Email · required</span>
                <input
                  required
                  autoFocus
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@yourbusiness.com"
                  data-testid="pricing-checkout-email"
                  style={{ ...inputStyle(), fontFamily: "inherit" }}
                />
              </label>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <label style={{ display: "grid", gap: 6 }}>
                  <span style={{ fontSize: 11, fontFamily: FONT_MONO, letterSpacing: "0.1em", textTransform: "uppercase", color: T.inkFaint }}>Your name</span>
                  <input
                    value={form.contact_name}
                    onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                    placeholder="Alex"
                    data-testid="pricing-checkout-name"
                    style={{ ...inputStyle(), fontFamily: "inherit" }}
                  />
                </label>
                <label style={{ display: "grid", gap: 6 }}>
                  <span style={{ fontSize: 11, fontFamily: FONT_MONO, letterSpacing: "0.1em", textTransform: "uppercase", color: T.inkFaint }}>Phone</span>
                  <input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    data-testid="pricing-checkout-phone"
                    style={{ ...inputStyle(), fontFamily: "inherit" }}
                  />
                </label>
              </div>

              <label style={{ display: "grid", gap: 6 }}>
                <span style={{ fontSize: 11, fontFamily: FONT_MONO, letterSpacing: "0.1em", textTransform: "uppercase", color: T.inkFaint }}>Business name</span>
                <input
                  value={form.business_name}
                  onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                  placeholder="Sullivan &amp; Sons Plumbing"
                  data-testid="pricing-checkout-business"
                  style={{ ...inputStyle(), fontFamily: "inherit" }}
                />
              </label>

              {/* Signup add-ons — multi-select with running total */}
              <div data-testid="pricing-checkout-addons" style={{ marginTop: 6 }}>
                <div style={{ fontSize: 11, fontFamily: FONT_MONO, letterSpacing: "0.12em", textTransform: "uppercase", color: T.inkFaint, marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span>Add-ons · optional</span>
                  <span style={{ color: T.inkMuted, textTransform: "none", letterSpacing: 0, fontSize: 11 }}>{activeAddons.length} selected</span>
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  {SIGNUP_ADDONS.map((a) => {
                    const checked = !!selectedAddons[a.id];
                    return (
                      <label
                        key={a.id}
                        data-testid={`pricing-checkout-addon-${a.id}`}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "auto 1fr auto",
                          gap: 12,
                          alignItems: "center",
                          padding: "12px 14px",
                          background: checked ? "rgba(229,83,59,0.06)" : T.canvas,
                          border: `1px solid ${checked ? T.coral : T.lineSoft}`,
                          cursor: "pointer",
                          transition: "background 120ms ease, border-color 120ms ease",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleAddon(a.id)}
                          data-testid={`pricing-checkout-addon-cb-${a.id}`}
                          style={{ width: 16, height: 16, accentColor: T.coral, cursor: "pointer" }}
                        />
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: T.ink, lineHeight: 1.3, display: "inline-flex", alignItems: "center", gap: 8 }}>
                            {a.name}
                            {a.highlight && (
                              <span style={{ fontSize: 9, fontFamily: FONT_MONO, letterSpacing: "0.1em", textTransform: "uppercase", color: T.brass, background: "rgba(200,149,67,0.14)", border: `1px solid ${T.brass}44`, padding: "1px 6px" }}>
                                Most popular
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 12, color: T.inkMuted, marginTop: 3, lineHeight: 1.4 }}>{a.blurb}</div>
                        </div>
                        <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                          {a.setup > 0 && (
                            <>
                              <div style={{ fontFamily: FONT_MONO, fontSize: 14, fontWeight: 700, color: T.coral }}>+${a.setup}</div>
                              <div style={{ fontSize: 9, fontFamily: FONT_MONO, letterSpacing: "0.1em", textTransform: "uppercase", color: T.inkFaint }}>one-time</div>
                            </>
                          )}
                          {a.monthly > 0 && (
                            <>
                              <div style={{ fontFamily: FONT_MONO, fontSize: 14, fontWeight: 700, color: T.ink }}>+${a.monthly}</div>
                              <div style={{ fontSize: 9, fontFamily: FONT_MONO, letterSpacing: "0.1em", textTransform: "uppercase", color: T.inkFaint }}>per month</div>
                            </>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>

                {/* Running total */}
                <div
                  data-testid="pricing-checkout-total"
                  style={{
                    marginTop: 12,
                    padding: "14px 16px",
                    background: T.ink,
                    color: T.cream,
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: 8,
                    fontFamily: FONT_MONO,
                  }}
                >
                  <span style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: T.brass }}>Due today</span>
                  <span data-testid="total-due-today" style={{ fontSize: 15, fontWeight: 700, textAlign: "right" }}>
                    ${checkoutPlan.setup + addonsSetupTotal}
                  </span>
                  <span style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(247,244,238,0.6)" }}>Then monthly</span>
                  <span data-testid="total-monthly" style={{ fontSize: 15, fontWeight: 700, textAlign: "right" }}>
                    ${checkoutPlan.price + addonsMonthlyTotal}/mo
                  </span>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="pricing-checkout-submit"
                style={{
                  marginTop: 8,
                  background: T.coral, color: T.cream, border: "none",
                  padding: "14px 22px", fontSize: 15, fontWeight: 700, cursor: submitting ? "wait" : "pointer",
                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                  fontFamily: "inherit", opacity: submitting ? 0.7 : 1,
                }}
              >
                {submitting ? (
                  <><RotateCw size={16} className="animate-spin" /> Redirecting to Stripe…</>
                ) : (
                  <><CreditCard size={16} /> Continue to secure checkout <ArrowUpRight size={14} /></>
                )}
              </button>

              <div style={{ fontSize: 11, color: T.inkFaint, textAlign: "center", marginTop: 4, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, fontFamily: FONT_MONO, letterSpacing: "0.04em" }}>
                <Lock size={11} /> Secure payment powered by Stripe · No card stored by us · Cancel anytime
              </div>
            </form>
          </div>
        </div>
      )}
    </PageShell>
  );
}
